<template>
  <div class="event-container">
    <!-- Header -->
    <div class="header-section">
      <div class="header-content">
        <div class="header-title">
          <i class="el-icon-s-flag"></i>
          <h1>Manajemen Kegiatan / Event</h1>
        </div>
        <p class="header-subtitle">Kelola data kegiatan dan event organisasi</p>
      </div>
      <div class="header-actions">
        <el-button icon="el-icon-refresh" @click="refreshEvents" :loading="loading">
          Refresh
        </el-button>
        <el-button type="primary" icon="el-icon-plus" @click="openCreateDialog">
          Tambah Event
        </el-button>
      </div>
    </div>

    <!-- Filter -->
    <div class="filter-section">
      <el-card shadow="hover">
        <div class="filter-content">
          <el-input
            v-model="searchQuery"
            placeholder="Cari kegiatan..."
            prefix-icon="el-icon-search"
            clearable
            style="width: 300px;"
          />
        </div>
      </el-card>
    </div>

    <!-- Table -->
    <el-card shadow="hover" class="table-card">
      <el-table :data="paginatedEvents" v-loading="loading">
        <el-table-column type="index" label="No" width="60" align="center">
          <template slot-scope="scope">
            {{ (currentPage - 1) * pageSize + scope.$index + 1 }}
          </template>
        </el-table-column>

        <el-table-column label="Judul Kegiatan" min-width="250">
          <template slot-scope="scope">
            <div class="activity-info">
              <div class="activity-name">
                <i class="el-icon-s-flag"></i>
                <strong>{{ scope.row.subject }}</strong>
              </div>
              <div class="status-badge" v-if="scope.row.isOnlineMeeting">
                <el-tag type="success" size="mini">
                  <i class="el-icon-video-camera"></i> Online Meeting
                </el-tag>
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="Tanggal & Waktu" width="240">
          <template slot-scope="scope">
            <div class="datetime-info">
              <div><i class="el-icon-date"></i> {{ scope.row.startDate }}</div>
              <div><i class="el-icon-time"></i> {{ scope.row.startTime }} - {{ scope.row.endTime }}</div>
              <div style="font-size: 11px; color: #909399;">
                <i class="el-icon-location-information"></i> {{ scope.row.startTimeZone || 'UTC' }}
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="Lokasi" min-width="200" show-overflow-tooltip>
          <template slot-scope="scope">
            <div>
              <i class="el-icon-location"></i> {{ scope.row.location || '-' }}
            </div>
          </template>
        </el-table-column>

        <el-table-column label="Penyelenggara" width="180" show-overflow-tooltip>
          <template slot-scope="scope">
            <div class="organizer-info">
              <div><i class="el-icon-user"></i> {{ scope.row.organizerName }}</div>
              <div style="font-size: 12px; color: #909399;">{{ scope.row.organizerEmail }}</div>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="Peserta" width="100" align="center">
          <template slot-scope="scope">
            <div class="capacity-info">
              <i class="el-icon-user"></i> {{ scope.row.attendeesCount || 0 }}
            </div>
          </template>
        </el-table-column>

        <el-table-column label="Aksi" width="330" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button type="info" icon="el-icon-view" size="mini" @click="openDetailDialog(scope.row)" circle title="Lihat Detail" />
            <el-button style="color: #fff; background-color: #67C23A; border-color: #67C23A;" icon="el-icon-user" size="mini" @click="openParticipantsDialog(scope.row)" circle title="Kelola Peserta" />
            <el-button style="color: #fff; background-color: #E6A23C; border-color: #E6A23C;" icon="el-icon-paperclip" size="mini" @click="openAttachmentsDialog(scope.row)" circle title="Kelola Dokumen" />
            <el-button style="color: #fff; background-color: #409EFF; border-color: #409EFF;" icon="el-icon-mobile-phone" size="mini" @click="openQRCodeDialog(scope.row)" circle title="Tampilkan QR Code" />
            <el-button type="primary" icon="el-icon-edit" size="mini" @click="openEditDialog(scope.row)" circle title="Edit Event" />
            <el-button type="danger" icon="el-icon-delete" size="mini" @click="handleDelete(scope.row)" circle title="Hapus Event" />
          </template>
        </el-table-column>
      </el-table>

      <!-- Pagination -->
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[5, 10, 20, 50]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="filteredEvents.length"
        />
      </div>
    </el-card>

    <!-- Create/Edit Dialog -->
    <el-dialog
      :title="dialogMode === 'create' ? 'Tambah Event Baru' : 'Edit Event'"
      :visible.sync="dialogVisible"
      width="800px"
      @close="resetForm"
    >
      <el-form :model="form" :rules="rules" ref="eventForm" label-width="180px">
        <el-form-item label="Subject/Title" prop="subject">
          <el-input v-model="form.subject" placeholder="Judul event (contoh: Workshop Digital Transformation)" />
        </el-form-item>

        <el-form-item label="Description" prop="body">
          <vue-editor 
            v-model="form.body"
            :editor-toolbar="customToolbar"
            placeholder="Masukkan deskripsi lengkap event"
          />
        </el-form-item>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="Start Date & Time" prop="startDateTime">
              <el-date-picker
                v-model="form.startDateTime"
                type="datetime"
                placeholder="Pilih tanggal & waktu mulai"
                style="width: 100%;"
                format="dd/MM/yyyy HH:mm"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="End Date & Time" prop="endDateTime">
              <el-date-picker
                v-model="form.endDateTime"
                type="datetime"
                placeholder="Pilih tanggal & waktu selesai"
                style="width: 100%;"
                format="dd/MM/yyyy HH:mm"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="Location" prop="location">
          <el-input v-model="form.location" placeholder="Lokasi event (contoh: Ruang Serba Guna Lantai 3)" />
        </el-form-item>

        <!-- Attendees only for create mode -->
        <el-form-item label="Attendees (Email)" prop="attendees" v-if="dialogMode === 'create'">
          <el-select
            v-model="form.attendees"
            multiple
            filterable
            remote
            reserve-keyword
            placeholder="Ketik nama untuk mencari peserta..."
            :remote-method="searchAttendees"
            :loading="searchingAttendees"
            style="width: 100%;"
          >
            <el-option
              v-for="user in attendeeOptions"
              :key="user.userPrincipalName"
              :label="`${user.displayName} (${user.userPrincipalName})`"
              :value="user.userPrincipalName"
            >
              <span style="float: left">{{ user.displayName }}</span>
              <span style="float: right; color: #8492a6; font-size: 13px">{{ user.userPrincipalName }}</span>
            </el-option>
          </el-select>
          <div class="form-hint">Ketik nama peserta untuk mencari dari Microsoft 365</div>
        </el-form-item>

        <!-- Info for edit mode -->
        <el-alert
          v-if="dialogMode === 'edit'"
          title="Kelola Peserta"
          type="info"
          description="Untuk mengelola peserta event, gunakan tombol 'Kelola Peserta' pada tabel event"
          :closable="false"
          show-icon
          style="margin-bottom: 20px;"
        />

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="Time Zone">
              <el-select v-model="form.timeZone" placeholder="Pilih timezone" style="width: 100%;">
                <el-option label="Asia/Jakarta (WIB)" value="Asia/Jakarta" />
                <el-option label="Asia/Makassar (WITA)" value="Asia/Makassar" />
                <el-option label="Asia/Jayapura (WIT)" value="Asia/Jayapura" />
                <el-option label="UTC" value="UTC" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="Show As">
              <el-select v-model="form.showAs" placeholder="Status ketersediaan" style="width: 100%;">
                <el-option label="Busy (Sibuk)" value="Busy" />
                <el-option label="Free (Tersedia)" value="Free" />
                <el-option label="Tentative (Mungkin)" value="Tentative" />
                <el-option label="Out of Office" value="Oof" />
                <el-option label="Working Elsewhere" value="WorkingElsewhere" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="Is All Day Event">
              <el-switch v-model="form.isAllDay" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="Is Online Meeting">
              <el-switch v-model="form.isOnlineMeeting" />
              <div class="form-hint">Teams meeting akan otomatis dibuat</div>
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="Reminder (minutes)" prop="reminderMinutesBeforeStart">
          <el-input-number 
            v-model="form.reminderMinutesBeforeStart" 
            :min="0" 
            :max="1440" 
            :step="5"
            style="width: 100%;" 
          />
          <div class="form-hint">Pengingat sebelum event dimulai (dalam menit)</div>
        </el-form-item>
      </el-form>

      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">Batal</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitting">
          {{ dialogMode === 'create' ? 'Simpan' : 'Update' }}
        </el-button>
      </span>
    </el-dialog>

    <!-- Detail Dialog -->
    <el-dialog title="Detail Event" :visible.sync="dialogDetailVisible" width="900px">
      <div class="detail-content" v-if="selectedEvent">
        <el-tabs v-model="activeTab" type="border-card">
          <!-- Tab 1: Informasi Event -->
          <el-tab-pane label="Informasi Event" name="info">
            <el-descriptions :column="2" border>
              <el-descriptions-item label="Judul Kegiatan" :span="2">
                <strong style="font-size: 16px;">{{ selectedEvent.subject }}</strong>
              </el-descriptions-item>
              <el-descriptions-item label="Tanggal">
                <i class="el-icon-date"></i> {{ selectedEvent.startDate }}
              </el-descriptions-item>
              <el-descriptions-item label="Waktu">
                <i class="el-icon-time"></i> {{ selectedEvent.startTime }} - {{ selectedEvent.endTime }}
              </el-descriptions-item>
              <el-descriptions-item label="Timezone">
                <i class="el-icon-location-information"></i> {{ selectedEvent.startTimeZone || 'UTC' }}
              </el-descriptions-item>
              <el-descriptions-item label="Lokasi" :span="2">
                <i class="el-icon-location"></i> {{ selectedEvent.location }}
              </el-descriptions-item>
              <el-descriptions-item label="Penyelenggara">
                {{ selectedEvent.organizerName }}
              </el-descriptions-item>
              <el-descriptions-item label="Email Penyelenggara">
                {{ selectedEvent.organizerEmail }}
              </el-descriptions-item>
              <el-descriptions-item label="Jumlah Peserta">
                <span style="color: #409EFF; font-weight: 600;">
                  <i class="el-icon-user"></i> {{ selectedEvent.attendeesCount }} Peserta
                </span>
              </el-descriptions-item>
              <el-descriptions-item label="Online Meeting">
                <el-tag :type="selectedEvent.isOnlineMeeting ? 'success' : 'info'" size="small">
                  {{ selectedEvent.isOnlineMeeting ? 'Ya' : 'Tidak' }}
                </el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="Link Meeting" :span="2" v-if="selectedEvent.isOnlineMeeting && selectedEvent.onlineMeetingUrl">
                <a :href="selectedEvent.onlineMeetingUrl" target="_blank" style="color: #409EFF;">
                  <i class="el-icon-link"></i> {{ selectedEvent.onlineMeetingUrl }}
                </a>
              </el-descriptions-item>
              <el-descriptions-item label="Deskripsi" :span="2" v-if="selectedEvent.body">
                <div v-html="selectedEvent.body"></div>
              </el-descriptions-item>
            </el-descriptions>
          </el-tab-pane>

          <!-- Tab 2: List Participations -->
          <el-tab-pane label="Daftar Peserta" name="participations">
            <div class="tab-header">
              <h3><i class="el-icon-user"></i> Daftar Peserta ({{ selectedEvent.attendeesCount || 0 }})</h3>
            </div>
            <el-table :data="getEventAttendees(selectedEvent)" style="width: 100%;" max-height="400">
              <el-table-column type="index" label="No" width="50" align="center" />
              <el-table-column prop="name" label="Nama Peserta" min-width="200" />
              <el-table-column prop="email" label="Email" min-width="200" />
              <el-table-column label="Status Respons" width="150" align="center">
                <template slot-scope="scope">
                  <el-tag :type="getResponseTypeColor(scope.row.responseStatus)" size="mini">
                    {{ getResponseStatusText(scope.row.responseStatus) }}
                  </el-tag>
                </template>
              </el-table-column>
            </el-table>
          </el-tab-pane>

          <!-- Tab 3: Dokumen Event -->
          <el-tab-pane label="Dokumen" name="documents">
            <div class="tab-header">
              <h3><i class="el-icon-folder-opened"></i> Dokumen Event</h3>
              <el-button
                v-if="isCurrentUserOrganizer(currentEventDetail)"
                type="primary"
                size="small"
                icon="el-icon-upload"
                @click="openAttachmentsFromDetail"
              >
                Kelola Dokumen
              </el-button>
            </div>

            <div v-if="loadingAttachments" style="text-align: center; padding: 40px;">
              <i class="el-icon-loading" style="font-size: 24px;"></i>
              <p style="margin-top: 10px; color: #909399;">Memuat dokumen...</p>
            </div>

            <el-empty
              v-else-if="!eventAttachments || eventAttachments.length === 0"
              description="Belum ada dokumen"
              :image-size="120"
            >
              <el-button
                v-if="isCurrentUserOrganizer(currentEventDetail)"
                type="primary"
                size="small"
                @click="openAttachmentsFromDetail"
              >
                Upload Dokumen Pertama
              </el-button>
            </el-empty>

            <el-table
              v-else
              :data="eventAttachments"
              border
              style="width: 100%;"
              max-height="450"
            >
              <el-table-column type="index" label="No" width="60" align="center" />
              
              <el-table-column label="Nama Dokumen" min-width="250">
                <template slot-scope="scope">
                  <i :class="getFileIcon(scope.row.name)" style="margin-right: 8px; font-size: 16px;"></i>
                  <span>{{ scope.row.name }}</span>
                </template>
              </el-table-column>

              <el-table-column label="Ukuran" width="100" align="center">
                <template slot-scope="scope">
                  {{ formatFileSize(scope.row.size) }}
                </template>
              </el-table-column>

              <el-table-column label="Tanggal Upload" width="160" align="center">
                <template slot-scope="scope">
                  {{ formatDateTime(scope.row.lastModifiedDateTime) }}
                </template>
              </el-table-column>

              <el-table-column label="Aksi" width="180" align="center" fixed="right">
                <template slot-scope="scope">
                  <el-button
                    type="primary"
                    size="mini"
                    icon="el-icon-download"
                    @click="handleDownloadFromDetail(scope.row)"
                  >
                    Download
                  </el-button>
                  <el-button
                    v-if="isCurrentUserOrganizer(currentEventDetail)"
                    type="danger"
                    size="mini"
                    icon="el-icon-delete"
                    @click="handleDeleteFromDetail(scope.row)"
                  >
                    Hapus
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
          </el-tab-pane>
        </el-tabs>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogDetailVisible = false">Tutup</el-button>
        <el-button
          type="warning"
          @click="openAttachmentsFromDetail"
        >
          <i class="el-icon-paperclip"></i> Kelola Dokumen
        </el-button>
        <el-button
          type="success"
          @click="openParticipantsFromDetail"
        >
          <i class="el-icon-user"></i> Kelola Peserta
        </el-button>
        <el-button type="primary" @click="openEditFromDetail">
          <i class="el-icon-edit"></i> Edit Event
        </el-button>
      </span>
    </el-dialog>

    <!-- QR Code Dialog -->
    <el-dialog title="QR Code untuk Daftar Hadir" :visible.sync="dialogQRCodeVisible" width="600px" center @opened="handleQRDialogOpened">
      <div v-if="selectedEventForQR" style="text-align: center;">
        <el-alert
          :title="`QR Code untuk: ${selectedEventForQR.subject}`"
          type="success"
          :closable="false"
          style="margin-bottom: 20px;"
        />
        
        <div style="margin-bottom: 20px;">
          <p style="color: #606266; margin-bottom: 10px;">
            <i class="el-icon-info"></i> Scan QR Code ini untuk membuka form check-in peserta
          </p>
          <p style="color: #909399; font-size: 12px;">
            Peserta dapat mengisi nama, email, dan nomor telepon mereka untuk melakukan check-in
          </p>
        </div>

        <!-- Loading State -->
        <div v-if="loadingQRCode" v-loading="true" style="height: 300px; display: flex; align-items: center; justify-content: center;">
          <span>Generating QR Code...</span>
        </div>

        <!-- QR Code Display -->
        <div v-else-if="qrCodeData && (qrCodeData.qrCodeUrl || qrCodeData.qrCodeBase64)" style="background: white; padding: 20px; display: inline-block; border-radius: 8px; box-shadow: 0 2px 12px rgba(0,0,0,0.1);">
          <img 
            :src="qrCodeData.qrCodeUrl || qrCodeData.qrCodeBase64" 
            alt="QR Code untuk Check-In" 
            style="max-width: 300px; width: 100%;" 
          />
        </div>

        <!-- Error State -->
        <div v-else-if="qrCodeError" style="padding: 20px;">
          <el-alert
            title="Gagal Generate QR Code"
            type="error"
            :description="qrCodeError"
            show-icon
          />
        </div>

        <!-- QR Code Details -->
        <div v-if="qrCodeData && !loadingQRCode" style="margin-top: 20px;">
          <p style="color: #909399; font-size: 12px;">
            Event: {{ selectedEventForQR.subject }}
          </p>
          <p style="color: #909399; font-size: 12px;">
            Tanggal: {{ selectedEventForQR.startDate }} {{ selectedEventForQR.startTime }}
          </p>
          <p v-if="qrCodeData.checkInUrl" style="color: #606266; font-weight: 600; margin-top: 10px; word-break: break-all;">
            <strong>URL Form Check-In:</strong><br/>
            <a :href="qrCodeData.checkInUrl" target="_blank" style="color: #409EFF;">{{ qrCodeData.checkInUrl }}</a>
          </p>
          <p v-if="qrCodeData.message" style="color: #67C23A; font-size: 12px; margin-top: 10px;">
            <i class="el-icon-success"></i> {{ qrCodeData.message }}
          </p>
        </div>

        <div v-if="qrCodeData && !loadingQRCode" style="margin-top: 20px;">
          <el-button type="primary" icon="el-icon-download" @click="downloadQRCode">
            Download QR Code
          </el-button>
          <el-button icon="el-icon-refresh" @click="regenerateQRCode">
            Regenerate
          </el-button>
          <el-button icon="el-icon-share" @click="copyAttendanceLink">
            Copy Link
          </el-button>
        </div>
      </div>
    </el-dialog>

    <!-- Manage Participants Dialog Component -->
    <ManageParticipantsDialog
      :visible.sync="dialogParticipantsVisible"
      :event="selectedEventForParticipants"
      :current-user-email="currentUserEmail"
      @participant-updated="handleParticipantUpdated"
    />

    <!-- Event Attachments Dialog Component -->
    <EventAttachmentsDialog
      :visible.sync="dialogAttachmentsVisible"
      :event="selectedEventForAttachments"
      :current-user-email="currentUserEmail"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import QRCode from 'qrcode';
import { VueEditor } from "vue2-editor/dist/vue2-editor.core.js";
import ManageParticipantsDialog from '@/components/AbsenEvent/ManageParticipantsDialog.vue';
import EventAttachmentsDialog from '@/components/AbsenEvent/EventAttachmentsDialog.vue';
// Import CSS styles
import '@/assets/styles/event-management.css';

export default {
  name: 'AbsenEventHome',
  components: {
    VueEditor,
    ManageParticipantsDialog,
    EventAttachmentsDialog
  },
  data() {
    return {
      loading: false,
      submitting: false,
      dialogVisible: false,
      dialogDetailVisible: false,
      dialogParticipantsVisible: false,
      dialogAttachmentsVisible: false,
      dialogQRCodeVisible: false,
      dialogMode: 'create',
      selectedEvent: null,
      selectedEventForParticipants: null,
      selectedEventForAttachments: null,
      selectedEventForQR: null,
      showAddParticipantForm: false,
      loadingParticipants: false,
      savingParticipant: false,
      loadingQRCode: false,
      qrCodeData: null,
      qrCodeError: null,
      eventParticipants: [],
      eventAttachments: [],
      loadingAttachments: false,
      searchQuery: '',
      currentPage: 1,
      pageSize: 10,
      activeTab: 'info',
      // User information for fetching events
      // Email format: username@lan.go.id
      currentUserEmail: '',
      currentUsername: '',
      // VueEditor custom toolbar
      customToolbar: [
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote', 'code-block'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'header': [false, 1, 2, 3, 4, 5, 6] }],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': [] }],
        ['link'],
        ['clean']
      ],
      // Attendees search
      attendeeOptions: [],
      searchingAttendees: false,
      participantForm: {
        email: '',
        name: ''
      },
      participantRules: {
        email: [{ required: true, message: 'Peserta wajib dipilih', trigger: 'change' }]
      },
      form: {
        id: null,
        subject: '',
        body: '',
        bodyType: 'HTML',
        startDateTime: '',
        endDateTime: '',
        timeZone: 'Asia/Jakarta',
        location: '',
        attendees: [],
        isAllDay: false,
        isOnlineMeeting: false,
        onlineMeetingProvider: 'teamsForBusiness',
        reminderMinutesBeforeStart: 15,
        showAs: 'Busy'
      },
      rules: {
        subject: [{ required: true, message: 'Subject wajib diisi', trigger: 'blur' }],
        startDateTime: [{ required: true, message: 'Start date & time wajib dipilih', trigger: 'change' }],
        endDateTime: [{ required: true, message: 'End date & time wajib dipilih', trigger: 'change' }],
        location: [{ required: true, message: 'Location wajib diisi', trigger: 'blur' }]
      },
      // Events from Microsoft 365 API
      events: []
    };
  },
  computed: {
    ...mapGetters('integration', ['isLoading', 'error']),
    currentEventDetail() {
      return this.selectedEvent;
    },
    filteredEvents() {
      let result = [...this.events];
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        result = result.filter(event =>
          (event.subject && event.subject.toLowerCase().includes(query)) ||
          (event.location && event.location.toLowerCase().includes(query)) ||
          (event.organizerName && event.organizerName.toLowerCase().includes(query))
        );
      }
      return result;
    },
    paginatedEvents() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.filteredEvents.slice(start, end);
    }
  },
  mounted() {
    // Fetch employee data from SDM service to get username
    this.fetchEmployeeAndInitialize();
  },
  methods: {
    ...mapActions('integration', [
      'getUserEvents', 
      'getEventsByCreator', 
      'createEvent', 
      'updateEvent',
      'checkInEvent', 
      'searchUsers',
      'getEventAttachments',
      'downloadEventAttachment',
      'deleteEventAttachment'
    ]),
    ...mapActions('user', ['get_user']),
    
    /**
     * Search attendees from Microsoft 365
     * @param {String} query - Search query (name)
     */
    async searchAttendees(query) {
      if (query !== '' && query.length >= 2) {
        this.searchingAttendees = true;
        try {
          const users = await this.searchUsers(query);
          this.attendeeOptions = users || [];
          console.log('Search results:', users);
        } catch (error) {
          console.error('Error searching attendees:', error);
          this.$message.error('Gagal mencari peserta: ' + (error.message || 'Unknown error'));
          this.attendeeOptions = [];
        } finally {
          this.searchingAttendees = false;
        }
      } else {
        this.attendeeOptions = [];
      }
    },
    
    /**
     * Fetch employee data and initialize email
     */
    async fetchEmployeeAndInitialize() {
      try {
        // Fetch employee data from /sdm/employee
        const response = await this.get_user();
        const employee = response.data || {};
        
        // Extract username from employee data
        // Adjust field name based on actual API response structure
        this.currentUsername = employee.username || employee.userName || employee.user_name || '';
        
        // Email format: username@lan.go.id
        if (this.currentUsername) {
          this.currentUserEmail = `${this.currentUsername}@lan.go.id`;
        }
        
        console.log('Employee data:', employee);
        console.log('Current user:', {
          username: this.currentUsername,
          email: this.currentUserEmail
        });
        
        // Fetch events after getting user info
        if (this.currentUserEmail) {
          this.fetchUserEvents();
        } else {
          this.$message.warning('Username tidak ditemukan dari data employee');
          console.error('Employee data:', employee);
        }
      } catch (error) {
        console.error('Error fetching employee data:', error);
        this.$message.error('Gagal memuat data employee: ' + (error.message || 'Unknown error'));
        
        // Fallback: try to get from store or localStorage
        this.fallbackGetUserEmail();
      }
    },
    
    /**
     * Fallback method to get user email from store/localStorage
     */
    fallbackGetUserEmail() {
      const userInfo = this.$store.state.info || {};
      
      // Set username
      this.currentUsername = userInfo.username || localStorage.getItem('username') || '';
      
      // Email format: username@lan.go.id
      if (userInfo.username) {
        this.currentUserEmail = `${userInfo.username}@lan.go.id`;
      } else if (userInfo.email) {
        this.currentUserEmail = userInfo.email;
      } else {
        // Try to get from localStorage
        const username = localStorage.getItem('username');
        if (username) {
          this.currentUserEmail = `${username}@lan.go.id`;
        } else {
          this.currentUserEmail = localStorage.getItem('userEmail') || '';
        }
      }
      
      console.log('Fallback - Current user:', {
        username: this.currentUsername,
        email: this.currentUserEmail
      });
      
      // Fetch events if email is available
      if (this.currentUserEmail) {
        this.fetchUserEvents();
      } else {
        this.$message.warning('User information not found. Please login again.');
      }
    },
    
    /**
     * Refresh events list manually
     */
    async refreshEvents() {
      await this.fetchUserEvents(false);
      this.$message.success('Data event berhasil direfresh');
    },

    /**
     * Fetch events from Microsoft 365 for current user
     * @param {Boolean} silent - If true, don't show error messages to user
     */
    async fetchUserEvents(silent = false) {
      if (!this.currentUserEmail) {
        if (!silent) {
          this.$message.error('Email pengguna tidak ditemukan');
        }
        return;
      }

      this.loading = true;
      try {
        // Fetch events created by this user (as organizer)
        const eventsData = await this.getEventsByCreator({
          userIdOrUpn: this.currentUserEmail,
          includeCheckIn: true
        });
        
        // Transform Microsoft 365 events to match the component's data structure
        this.events = this.transformMicrosoftEvents(eventsData);
        
        if (this.events.length === 0 && !silent) {
          this.$message.info('Tidak ada event yang ditemukan');
        }
      } catch (error) {
        console.error('Error fetching events:', error);
        if (!silent) {
          this.$message.error('Gagal memuat data event: ' + (error.message || 'Unknown error'));
        }
        // Keep empty array if fetch fails
        this.events = [];
      } finally {
        this.loading = false;
      }
    },

    /**
     * Transform Microsoft 365 event format to component format
     * @param {Array} microsoftEvents - Events from Microsoft 365 API
     * @returns {Array} Transformed events
     */
    transformMicrosoftEvents(microsoftEvents) {
      if (!Array.isArray(microsoftEvents)) {
        return [];
      }

      return microsoftEvents.map((event, index) => {
        // Parse date and time from Microsoft format with timezone info
        const startDateTime = event.start?.dateTime || '';
        const endDateTime = event.end?.dateTime || '';
        const startTimeZone = event.start?.timeZone || 'UTC';
        const endTimeZone = event.end?.timeZone || 'UTC';
        
        // Convert ISO datetime to readable format with timezone consideration
        const startDate = this.formatDateWithTimeZone(startDateTime, startTimeZone);
        const startTime = this.formatTimeWithTimeZone(startDateTime, startTimeZone);
        const endTime = this.formatTimeWithTimeZone(endDateTime, endTimeZone);
        
        // Get organizer info
        const organizerName = event.organizer?.name || 'Unknown';
        const organizerEmail = event.organizer?.email || '';
        
        // Count attendees
        const attendeesCount = event.attendees?.length || 0;
        
        return {
          id: event.id,
          subject: event.subject || 'Untitled Event',
          startDate: startDate,
          startTime: startTime,
          endTime: endTime,
          startDateTime: startDateTime, // Keep for sorting/filtering
          endDateTime: endDateTime,
          startTimeZone: startTimeZone, // Store timezone info
          endTimeZone: endTimeZone,
          location: event.locationName || '-',
          organizerName: organizerName,
          organizerEmail: organizerEmail,
          attendeesCount: attendeesCount,
          isOnlineMeeting: event.isOnlineMeeting || false,
          onlineMeetingUrl: event.onlineMeetingUrl || null,
          body: event.body || '',
          // Store original Microsoft event data for detail view
          _rawEvent: event
        };
      });
    },

    /**
     * Format ISO datetime to DD/MM/YYYY with timezone consideration
     * Converts UTC datetime to specified timezone
     * @param {string} isoDateString - ISO datetime string (in UTC from Microsoft)
     * @param {string} timeZone - Target timezone (e.g., 'Asia/Jakarta')
     * @returns {string} Formatted date
     */
    formatDateWithTimeZone(isoDateString, timeZone = 'UTC') {
      if (!isoDateString) return '-';
      try {
        // Microsoft returns datetime in UTC format
        // We need to parse it as UTC and convert to the specified timezone
        let dateStr = isoDateString;
        
        // Ensure datetime is treated as UTC
        if (!dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.includes('-', 10)) {
          dateStr = dateStr.split('.')[0] + 'Z'; // Remove milliseconds and add Z
        }
        
        // Parse as UTC
        const utcDate = new Date(dateStr);
        
        // Get timezone offset for the target timezone
        // Asia/Jakarta is UTC+7, so we need to add 7 hours to UTC time
        let offsetHours = 0;
        if (timeZone === 'Asia/Jakarta') {
          offsetHours = 7; // WIB = UTC+7
        } else if (timeZone === 'Asia/Makassar') {
          offsetHours = 8; // WITA = UTC+8
        } else if (timeZone === 'Asia/Jayapura') {
          offsetHours = 9; // WIT = UTC+9
        } else if (timeZone === 'UTC') {
          offsetHours = 0;
        }
        
        // Convert UTC to target timezone by adding offset
        const localDate = new Date(utcDate.getTime() + (offsetHours * 60 * 60 * 1000));
        
        const day = String(localDate.getUTCDate()).padStart(2, '0');
        const month = String(localDate.getUTCMonth() + 1).padStart(2, '0');
        const year = localDate.getUTCFullYear();
        
        return `${day}/${month}/${year}`;
      } catch (error) {
        console.error('Error formatting date:', error);
        return '-';
      }
    },

    /**
     * Format ISO datetime to HH:MM with timezone consideration
     * Converts UTC datetime to specified timezone
     * @param {string} isoDateString - ISO datetime string (in UTC from Microsoft)
     * @param {string} timeZone - Target timezone (e.g., 'Asia/Jakarta')
     * @returns {string} Formatted time
     */
    formatTimeWithTimeZone(isoDateString, timeZone = 'UTC') {
      if (!isoDateString) return '-';
      try {
        // Microsoft returns datetime in UTC format
        // We need to parse it as UTC and convert to the specified timezone
        let dateStr = isoDateString;
        
        // Ensure datetime is treated as UTC
        if (!dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.includes('-', 10)) {
          dateStr = dateStr.split('.')[0] + 'Z'; // Remove milliseconds and add Z
        }
        
        // Parse as UTC
        const utcDate = new Date(dateStr);
        
        // Get timezone offset for the target timezone
        let offsetHours = 0;
        if (timeZone === 'Asia/Jakarta') {
          offsetHours = 7; // WIB = UTC+7
        } else if (timeZone === 'Asia/Makassar') {
          offsetHours = 8; // WITA = UTC+8
        } else if (timeZone === 'Asia/Jayapura') {
          offsetHours = 9; // WIT = UTC+9
        } else if (timeZone === 'UTC') {
          offsetHours = 0;
        }
        
        // Convert UTC to target timezone by adding offset
        const localDate = new Date(utcDate.getTime() + (offsetHours * 60 * 60 * 1000));
        
        // Use getUTCHours/Minutes because we shifted the timestamp
        // After adding offset, the UTC components represent the target timezone
        const hours = String(localDate.getUTCHours()).padStart(2, '0');
        const minutes = String(localDate.getUTCMinutes()).padStart(2, '0');
        
        // Debug log
        console.log('formatTimeWithTimeZone:', {
          input: isoDateString,
          timezone: timeZone,
          utcTime: utcDate.toISOString(),
          offsetHours: offsetHours,
          localDate: localDate.toISOString(),
          convertedTime: `${hours}:${minutes}`
        });
        
        return `${hours}:${minutes}`;
      } catch (error) {
        console.error('Error formatting time:', error);
        return '-';
      }
    },

    /**
     * Format ISO datetime to DD/MM/YYYY (backward compatibility - auto-detect browser timezone)
     * Handles UTC datetime strings by converting to browser's local timezone
     */
    formatDate(isoDateString) {
      if (!isoDateString) return '-';
      try {
        // Parse as UTC by appending 'Z' if not present
        // Microsoft API sends UTC times without 'Z' suffix
        let dateStr = isoDateString;
        if (!dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.includes('T')) {
          dateStr = dateStr + 'Z';
        } else if (dateStr.includes('T') && !dateStr.endsWith('Z') && !dateStr.includes('+')) {
          dateStr = dateStr + 'Z';
        }
        
        const date = new Date(dateStr);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      } catch (error) {
        return '-';
      }
    },

    /**
     * Format ISO datetime to HH:MM (auto-detect browser timezone)
     * Handles UTC datetime strings by converting to browser's local timezone
     */
    formatTime(isoDateString) {
      if (!isoDateString) return '-';
      try {
        // Parse as UTC by appending 'Z' if not present
        // Microsoft API sends UTC times without 'Z' suffix
        let dateStr = isoDateString;
        if (!dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.includes('T')) {
          dateStr = dateStr + 'Z';
        } else if (dateStr.includes('T') && !dateStr.endsWith('Z') && !dateStr.includes('+')) {
          dateStr = dateStr + 'Z';
        }
        
        const date = new Date(dateStr);
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${hours}:${minutes}`;
      } catch (error) {
        return '-';
      }
    },

    /**
     * Check if event is currently active (ongoing or in the future)
     */
    isEventActive(startDateTime, endDateTime) {
      if (!startDateTime) return false;
      try {
        const now = new Date();
        const start = new Date(startDateTime);
        const end = endDateTime ? new Date(endDateTime) : null;
        
        // Event is active if it hasn't ended yet
        if (end) {
          return now < end;
        }
        // If no end time, check if start is in the future
        return now < start;
      } catch (error) {
        return false;
      }
    },

    /**
     * Get attendees list from event
     */
    getEventAttendees(event) {
      if (!event || !event._rawEvent || !event._rawEvent.attendees) {
        return [];
      }
      
      return event._rawEvent.attendees.map((attendee, index) => ({
        id: index + 1,
        name: attendee.name || attendee.emailAddress?.name || '-',
        email: attendee.email || attendee.emailAddress?.address || '-',
        responseStatus: attendee.status?.response || 'none'
      }));
    },

    /**
     * Get response status text in Indonesian
     */
    getResponseStatusText(status) {
      const statusMap = {
        'accepted': 'Diterima',
        'declined': 'Ditolak',
        'tentativelyAccepted': 'Mungkin',
        'notResponded': 'Belum Merespons',
        'none': 'Belum Merespons'
      };
      return statusMap[status] || 'Belum Merespons';
    },

    /**
     * Get response status color
     */
    getResponseTypeColor(status) {
      const colorMap = {
        'accepted': 'success',
        'declined': 'danger',
        'tentativelyAccepted': 'warning',
        'notResponded': 'info',
        'none': 'info'
      };
      return colorMap[status] || 'info';
    },

    /**
     * Handle check-in to an event
     */
    async handleCheckIn(eventId) {
      if (!this.currentUserEmail) {
        this.$message.error('Email pengguna tidak ditemukan');
        return;
      }

      try {
        await this.checkInEvent({
          userIdOrUpn: this.currentUserEmail,
          eventId: eventId,
          checkInData: {
            timestamp: new Date().toISOString(),
            attendeeEmail: this.currentUserEmail
          }
        });
        
        this.$message.success('Check-in berhasil!');
        // Refresh events to get updated check-in status
        await this.fetchUserEvents();
      } catch (error) {
        console.error('Error checking in:', error);
        this.$message.error('Gagal melakukan check-in: ' + (error.message || 'Unknown error'));
      }
    },

    resetUploadForm() {
      this.uploadForm = {
        letterNumber: '',
        letterTitle: '',
        letterType: '',
        issuedBy: '',
        issuedDate: '',
        file: null
      };
      this.fileList = [];
      this.selectedEventForUpload = null;
      if (this.$refs.uploadForm) {
        this.$refs.uploadForm.clearValidate();
      }
    },
    handleSizeChange(val) {
      this.pageSize = val;
      this.currentPage = 1;
    },
    handleCurrentChange(val) {
      this.currentPage = val;
    },
    openCreateDialog() {
      this.dialogMode = 'create';
      this.dialogVisible = true;
    },
    openDetailDialog(row) {
      this.selectedEvent = { ...row };
      this.dialogDetailVisible = true;
      this.activeTab = 'info'; // Reset to info tab
      this.loadEventAttachments(row); // Load attachments when opening detail
    },
    openEditDialog(row) {
      this.dialogMode = 'edit';
      // Populate form with data from _rawEvent for complete data
      // Note: body in Microsoft 365 API is an object { content, contentType }, extract content
      const bodyContent = row._rawEvent?.body?.content || row._rawEvent?.body || row.body || '';
      
      // Parse datetime string to Date object for el-date-picker (without value-format)
      // Microsoft returns UTC datetime, we need to convert it to the user's timezone
      const parseToDateObject = (isoDateTime, timeZone = 'UTC') => {
        if (!isoDateTime) return null;
        try {
          // Get timezone offset for the target timezone
          let offsetHours = 0;
          if (timeZone === 'Asia/Jakarta') {
            offsetHours = 7; // WIB = UTC+7
          } else if (timeZone === 'Asia/Makassar') {
            offsetHours = 8; // WITA = UTC+8
          } else if (timeZone === 'Asia/Jayapura') {
            offsetHours = 9; // WIT = UTC+9
          } else if (timeZone === 'UTC') {
            offsetHours = 0;
          }
          
          // Extract datetime parts without timezone info
          let cleanDateTime = isoDateTime.split('.')[0]; // Remove milliseconds
          cleanDateTime = cleanDateTime.split('+')[0]; // Remove +timezone
          cleanDateTime = cleanDateTime.split('Z')[0]; // Remove Z (UTC)
          
          // Add Z to treat as UTC
          const utcDateStr = cleanDateTime + 'Z';
          
          // Parse as UTC
          const utcDate = new Date(utcDateStr);
          
          // Add timezone offset to convert to local time
          // For example: 02:00 UTC + 7 hours = 09:00 Jakarta time
          const localTime = utcDate.getTime() + (offsetHours * 60 * 60 * 1000);
          const localDate = new Date(localTime);
          
          // Create Date object in LOCAL timezone with the converted time
          // This is what el-date-picker expects
          return new Date(
            localDate.getUTCFullYear(),
            localDate.getUTCMonth(),
            localDate.getUTCDate(),
            localDate.getUTCHours(),
            localDate.getUTCMinutes(),
            localDate.getUTCSeconds()
          );
        } catch (error) {
          console.error('Error parsing datetime:', isoDateTime, error);
          return null;
        }
      };
      
      // Get timezone for parsing datetime
      const eventTimeZone = row._rawEvent?.start?.timeZone || row.startTimeZone || 'Asia/Jakarta';
      
      this.form = {
        id: row.id,
        subject: row.subject,
        body: bodyContent,
        bodyType: 'HTML',
        startDateTime: parseToDateObject(row.startDateTime, eventTimeZone),
        endDateTime: parseToDateObject(row.endDateTime, eventTimeZone),
        timeZone: eventTimeZone,
        location: row.location,
        attendees: row._rawEvent?.attendees?.map(a => a.email) || [],
        isAllDay: row._rawEvent?.isAllDay || false,
        isOnlineMeeting: row.isOnlineMeeting || false,
        onlineMeetingProvider: 'teamsForBusiness',
        reminderMinutesBeforeStart: row._rawEvent?.reminderMinutesBeforeStart || 15,
        showAs: row._rawEvent?.showAs || 'Busy'
      };
      
      // Debug: Log datetime values
      console.log('Edit Event - Original datetime:', {
        startDateTime: row.startDateTime,
        endDateTime: row.endDateTime,
        timeZone: eventTimeZone
      });
      console.log('Edit Event - Parsed to Date object:', {
        startDateTime: this.form.startDateTime,
        endDateTime: this.form.endDateTime
      });
      
      this.dialogVisible = true;
    },
    openEditFromDetail() {
      this.dialogDetailVisible = false;
      this.openEditDialog(this.selectedEvent);
    },
    openParticipantsFromDetail() {
      this.dialogDetailVisible = false;
      this.openParticipantsDialog(this.selectedEvent);
    },
    openAttachmentsDialog(row) {
      this.selectedEventForAttachments = { ...row };
      this.dialogAttachmentsVisible = true;
    },
    openAttachmentsFromDetail() {
      this.dialogDetailVisible = false;
      this.openAttachmentsDialog(this.selectedEvent);
    },
    handleParticipantUpdated() {
      // Refresh events list after participant update
      this.fetchUserEvents(true); // silent refresh
    },
    handleSubmit() {
      this.$refs.eventForm.validate(async (valid) => {
        if (valid) {
          // Validate email is available
          if (!this.currentUserEmail) {
            this.$message.error('Email pengguna tidak ditemukan. Silakan login ulang.');
            return;
          }

          // Validate date range
          if (this.form.startDateTime && this.form.endDateTime) {
            const start = new Date(this.form.startDateTime);
            const end = new Date(this.form.endDateTime);
            if (end <= start) {
              this.$message.error('End date & time harus setelah start date & time');
              return;
            }
          }

          this.submitting = true;
          
          try {
            if (this.dialogMode === 'create') {
              // Format data to match CreateEventRequest DTO
              const createEventRequest = {
                subject: this.form.subject,
                body: this.form.body || '',  // Ensure body is never null
                bodyType: this.form.bodyType || 'HTML',
                startDateTime: this.formatDateTimeForAPI(this.form.startDateTime),
                endDateTime: this.formatDateTimeForAPI(this.form.endDateTime),
                timeZone: this.form.timeZone,
                location: this.form.location,
                attendees: this.form.attendees,
                isAllDay: this.form.isAllDay,
                isOnlineMeeting: this.form.isOnlineMeeting,
                onlineMeetingProvider: this.form.isOnlineMeeting ? this.form.onlineMeetingProvider : null,
                reminderMinutesBeforeStart: this.form.reminderMinutesBeforeStart,
                showAs: this.form.showAs
              };

              console.log('Creating event with data:', createEventRequest);
              console.log('Body content:', this.form.body);
              console.log('User email:', this.currentUserEmail);

              // Call createEvent action from Vuex
              const response = await this.createEvent({
                userIdOrUpn: this.currentUserEmail,
                eventData: createEventRequest
              });

              console.log('Event created successfully:', response);
              
              this.$message({
                type: 'success',
                message: 'Event berhasil ditambahkan ke Microsoft 365! Event baru akan muncul dalam beberapa saat.',
                duration: 5000
              });
              
              // Close dialog and reset form first
              this.dialogVisible = false;
              this.resetForm();
              
              // Refresh events list in background (silent mode - no error messages)
              setTimeout(async () => {
                try {
                  await this.fetchUserEvents(true);
                } catch (refreshError) {
                  console.warn('Failed to refresh events list:', refreshError);
                  // Silently fail - user can manually refresh the page
                }
              }, 2000); // Wait 2 seconds before refreshing to allow Microsoft 365 to sync
              
            } else {
              // Edit mode - Update existing event
              const updateEventRequest = {
                subject: this.form.subject,
                body: this.form.body || '',
                bodyType: this.form.bodyType || 'HTML',
                startDateTime: this.formatDateTimeForAPI(this.form.startDateTime),
                endDateTime: this.formatDateTimeForAPI(this.form.endDateTime),
                timeZone: this.form.timeZone,
                location: this.form.location,
                attendees: this.form.attendees,
                isAllDay: this.form.isAllDay,
                isOnlineMeeting: this.form.isOnlineMeeting,
                onlineMeetingProvider: this.form.isOnlineMeeting ? this.form.onlineMeetingProvider : null,
                reminderMinutesBeforeStart: this.form.reminderMinutesBeforeStart,
                showAs: this.form.showAs
              };

              console.log('Updating event with data:', updateEventRequest);
              console.log('Event ID:', this.form.id);
              console.log('User email:', this.currentUserEmail);

              // Call updateEvent action from Vuex
              const response = await this.updateEvent({
                userIdOrUpn: this.currentUserEmail,
                eventId: this.form.id,
                eventData: updateEventRequest
              });

              console.log('Event updated successfully:', response);
              
              this.$message({
                type: 'success',
                message: 'Event berhasil diupdate di Microsoft 365! Perubahan akan muncul dalam beberapa saat.',
                duration: 5000
              });
              
              // Close dialog and reset form first
              this.dialogVisible = false;
              this.resetForm();
              
              // Refresh events list in background
              setTimeout(async () => {
                try {
                  await this.fetchUserEvents(true);
                } catch (refreshError) {
                  console.warn('Failed to refresh events list:', refreshError);
                }
              }, 2000);
            }
          } catch (error) {
            console.error('Error saving event:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
            const action = this.dialogMode === 'create' ? 'membuat' : 'mengupdate';
            this.$message.error(`Gagal ${action} event: ${errorMessage}`);
          } finally {
            this.submitting = false;
          }
        } else {
          this.$message.error('Mohon lengkapi form dengan benar');
          return false;
        }
      });
    },

    /**
     * Format datetime from form to ISO 8601 format for API
     * Input: Date object (from el-date-picker without value-format)
     * Output: "2024-12-09T10:00:00" (ISO 8601 without timezone)
     * Note: The API expects datetime in the event's timezone (Asia/Jakarta)
     */
    formatDateTimeForAPI(dateTimeValue) {
      if (!dateTimeValue) return null;
      
      try {
        let dateObj;
        
        // Handle both Date object and string
        if (dateTimeValue instanceof Date) {
          dateObj = dateTimeValue;
        } else if (typeof dateTimeValue === 'string') {
          // Legacy support for string format
          return dateTimeValue.replace(' ', 'T');
        } else {
          return null;
        }
        
        // Format Date object to ISO 8601 string WITHOUT timezone conversion
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        const hours = String(dateObj.getHours()).padStart(2, '0');
        const minutes = String(dateObj.getMinutes()).padStart(2, '0');
        const seconds = String(dateObj.getSeconds()).padStart(2, '0');
        
        const formatted = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
        
        console.log('formatDateTimeForAPI:', {
          input: dateTimeValue,
          output: formatted,
          localTimeString: dateObj.toLocaleString('id-ID')
        });
        
        return formatted;
      } catch (error) {
        console.error('Error formatting datetime for API:', dateTimeValue, error);
        return null;
      }
    },
    handleDelete(row) {
      this.$confirm('Apakah Anda yakin ingin menghapus event ini?', 'Konfirmasi', {
        confirmButtonText: 'Ya, Hapus',
        cancelButtonText: 'Batal',
        type: 'warning'
      }).then(() => {
        const index = this.events.findIndex(e => e.id === row.id);
        if (index !== -1) {
          this.events.splice(index, 1);
          this.$message.success('Event berhasil dihapus!');
        }
      }).catch(() => {
        this.$message.info('Penghapusan dibatalkan');
      });
    },
    openParticipantsDialog(row) {
      this.selectedEventForParticipants = { ...row };
      // Load participants from raw event attendees
      this.loadParticipantsFromEvent();
      this.dialogParticipantsVisible = true;
    },
    openAttachmentsDialog(row) {
      this.selectedEventForAttachments = { ...row };
      this.dialogAttachmentsVisible = true;
    },
    openParticipantsFromDetail() {
      this.selectedEventForParticipants = { ...this.selectedEvent };
      this.dialogDetailVisible = false;
      this.dialogParticipantsVisible = true;
    },
    openAttachmentsFromDetail() {
      this.selectedEventForAttachments = { ...this.selectedEvent };
      this.dialogDetailVisible = false;
      this.dialogAttachmentsVisible = true;
    },
    handleParticipantUpdated() {
      // Refresh events list after participant update
      this.fetchUserEvents(true);
    },

    /**
     * Load event attachments
     */
    async loadEventAttachments(event) {
      if (!event?.id || !event?.organizerEmail) {
        console.log('Event or organizer email not available');
        return;
      }

      this.loadingAttachments = true;
      this.eventAttachments = [];
      
      try {
        const attachments = await this.getEventAttachments({
          userIdOrUpn: event.organizerEmail,
          eventId: event.id
        });
        this.eventAttachments = attachments || [];
        console.log('Loaded attachments:', this.eventAttachments);
      } catch (error) {
        console.error('Error loading attachments:', error);
        this.$message.error('Gagal memuat dokumen: ' + (error.message || 'Unknown error'));
        this.eventAttachments = [];
      } finally {
        this.loadingAttachments = false;
      }
    },

    /**
     * Handle download from detail dialog
     */
    async handleDownloadFromDetail(attachment) {
      if (!this.selectedEvent?.id || !this.selectedEvent?.organizerEmail) {
        this.$message.error('Informasi event tidak lengkap');
        return;
      }

      try {
        await this.downloadEventAttachment({
          userIdOrUpn: this.selectedEvent.organizerEmail,
          eventId: this.selectedEvent.id,
          attachmentId: attachment.id,
          fileName: attachment.name
        });
        this.$message.success('Dokumen berhasil didownload!');
      } catch (error) {
        console.error('Error downloading attachment:', error);
        this.$message.error('Gagal mendownload dokumen: ' + (error.message || 'Unknown error'));
      }
    },

    /**
     * Handle delete from detail dialog
     */
    async handleDeleteFromDetail(attachment) {
      if (!this.selectedEvent?.id || !this.selectedEvent?.organizerEmail) {
        this.$message.error('Informasi event tidak lengkap');
        return;
      }

      this.$confirm(
        `Apakah Anda yakin ingin menghapus dokumen "${attachment.name}"?`,
        'Konfirmasi Hapus',
        {
          confirmButtonText: 'Ya, Hapus',
          cancelButtonText: 'Batal',
          type: 'warning'
        }
      ).then(async () => {
        try {
          await this.deleteEventAttachment({
            userIdOrUpn: this.selectedEvent.organizerEmail,
            eventId: this.selectedEvent.id,
            attachmentId: attachment.id
          });

          this.$message.success('Dokumen berhasil dihapus!');
          
          // Reload attachments
          await this.loadEventAttachments(this.selectedEvent);
        } catch (error) {
          console.error('Error deleting attachment:', error);
          this.$message.error('Gagal menghapus dokumen: ' + (error.message || 'Unknown error'));
        }
      }).catch(() => {
        // User cancelled
      });
    },

    /**
     * Check if current user is organizer
     */
    isCurrentUserOrganizer(event) {
      if (!event || !this.currentUserEmail) {
        return false;
      }
      return event.organizerEmail?.toLowerCase() === this.currentUserEmail.toLowerCase();
    },

    /**
     * Get file icon based on extension
     */
    getFileIcon(fileName) {
      if (!fileName) return 'el-icon-document';
      
      const extension = fileName.split('.').pop().toLowerCase();
      const iconMap = {
        pdf: 'el-icon-document',
        doc: 'el-icon-document',
        docx: 'el-icon-document',
        xls: 'el-icon-tickets',
        xlsx: 'el-icon-tickets',
        ppt: 'el-icon-reading',
        pptx: 'el-icon-reading',
        jpg: 'el-icon-picture',
        jpeg: 'el-icon-picture',
        png: 'el-icon-picture',
        gif: 'el-icon-picture',
        zip: 'el-icon-folder',
        rar: 'el-icon-folder',
        txt: 'el-icon-document',
        default: 'el-icon-document'
      };
      return iconMap[extension] || iconMap.default;
    },

    /**
     * Format file size
     */
    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    /**
     * Format date with time (auto-detect browser timezone)
     * Handles UTC datetime strings by converting to browser's local timezone
     * Used for displaying document lastModifiedDateTime
     */
    formatDateTime(dateString) {
      if (!dateString) return '-';
      
      try {
        // Parse as UTC by appending 'Z' if not present
        let dateStr = dateString;
        if (!dateStr.endsWith('Z') && !dateStr.includes('+') && dateStr.includes('T') && !dateStr.includes('T') === false) {
          dateStr = dateStr + 'Z';
        } else if (dateStr.includes('T') && !dateStr.endsWith('Z') && !dateStr.includes('+')) {
          dateStr = dateStr + 'Z';
        }
        
        const date = new Date(dateStr);
        
        // Check if date is valid
        if (isNaN(date.getTime())) {
          return '-';
        }
        
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}`;
      } catch (error) {
        console.error('Error formatting date:', dateString, error);
        return '-';
      }
    },

    loadParticipantsFromEvent() {
      // Transform attendees from Microsoft 365 format to UI format
      if (this.selectedEventForParticipants && this.selectedEventForParticipants._rawEvent && this.selectedEventForParticipants._rawEvent.attendees) {
        this.eventParticipants = this.selectedEventForParticipants._rawEvent.attendees.map(attendee => ({
          name: attendee.name || 'Unknown',
          email: attendee.email || '',
          responseStatus: attendee.responseStatus || 'none'
        }));
      } else {
        this.eventParticipants = [];
      }
    },
    closeParticipantsDialog() {
      this.showAddParticipantForm = false;
      this.resetParticipantForm();
    },
    openAddParticipantForm() {
      this.showAddParticipantForm = true;
    },
    cancelAddParticipant() {
      this.showAddParticipantForm = false;
      this.resetParticipantForm();
    },
    handleParticipantSelected(email) {
      // When user selects from search, populate name from options
      const selected = this.attendeeOptions.find(opt => opt.userPrincipalName === email);
      if (selected) {
        this.participantForm.name = selected.displayName;
      }
    },
    async addParticipant() {
      this.$refs.participantForm.validate(async (valid) => {
        if (valid) {
          // Check if participant already exists
          const existingParticipant = this.eventParticipants.find(p => p.email === this.participantForm.email);
          if (existingParticipant) {
            this.$message.warning('Peserta sudah terdaftar dalam event ini!');
            return;
          }

          this.savingParticipant = true;
          try {
            // Get current organizer email - MUST use current user's email for PATCH
            const organizerEmail = this.selectedEventForParticipants.organizerEmail;
            const eventId = this.selectedEventForParticipants.id;
            
            if (!organizerEmail || !eventId) {
              this.$message.error('Data event tidak lengkap');
              return;
            }

            // Check if current user is the organizer
            if (organizerEmail !== this.currentUserEmail) {
              this.$message.error('Hanya organizer yang dapat mengelola peserta event ini');
              return;
            }

            // Create updated attendees array - backend expects array of email strings
            const currentAttendees = this.selectedEventForParticipants._rawEvent.attendees || [];
            const updatedAttendees = [
              ...currentAttendees.map(a => a.email),
              this.participantForm.email
            ];

            // Prepare update payload
            const updatePayload = {
              subject: this.selectedEventForParticipants.subject,
              body: this.selectedEventForParticipants._rawEvent.body?.content || this.selectedEventForParticipants._rawEvent.body || '',
              bodyType: 'HTML',
              startDateTime: this.selectedEventForParticipants.startDateTime,
              endDateTime: this.selectedEventForParticipants.endDateTime,
              timeZone: 'Asia/Jakarta',
              location: this.selectedEventForParticipants.location || '',
              attendees: updatedAttendees,
              isAllDay: this.selectedEventForParticipants._rawEvent.isAllDay || false,
              isOnlineMeeting: this.selectedEventForParticipants.isOnlineMeeting || false,
              onlineMeetingProvider: 'teamsForBusiness',
              reminderMinutesBeforeStart: this.selectedEventForParticipants._rawEvent.reminderMinutesBeforeStart || 15,
              showAs: this.selectedEventForParticipants._rawEvent.showAs || 'Busy'
            };

            console.log('Updating event:', {
              userIdOrUpn: this.currentUserEmail,
              eventId: eventId,
              updatePayload: updatePayload
            });

            // Call PATCH endpoint via Vuex action - use current user's email
            await this.updateEvent({
              userIdOrUpn: this.currentUserEmail,
              eventId: eventId,
              eventData: updatePayload
            });

            this.$message.success('Peserta berhasil ditambahkan!');
            
            // Reload participants and refresh events
            await this.fetchUserEvents(true); // silent refresh
            
            // Find updated event and reload participants
            const updatedEvent = this.events.find(e => e.id === eventId);
            if (updatedEvent) {
              this.selectedEventForParticipants = updatedEvent;
              this.loadParticipantsFromEvent();
            }
            
            this.showAddParticipantForm = false;
            this.resetParticipantForm();
          } catch (error) {
            console.error('Error adding participant:', error);
            
            // Handle specific error cases
            if (error.response?.status === 403) {
              this.$message.error('Anda tidak memiliki izin untuk mengubah event ini. Hanya organizer yang dapat mengelola peserta.');
            } else if (error.response?.status === 404) {
              this.$message.error('Event tidak ditemukan');
            } else {
              this.$message.error('Gagal menambahkan peserta: ' + (error.message || 'Unknown error'));
            }
          } finally {
            this.savingParticipant = false;
          }
        } else {
          this.$message.error('Mohon lengkapi form dengan benar');
          return false;
        }
      });
    },
    async removeParticipant(row) {
      this.loadingParticipants = true;
      try {
        // Get current organizer email - MUST use current user's email for PATCH
        const organizerEmail = this.selectedEventForParticipants.organizerEmail;
        const eventId = this.selectedEventForParticipants.id;
        
        if (!organizerEmail || !eventId) {
          this.$message.error('Data event tidak lengkap');
          return;
        }

        // Check if current user is the organizer
        if (organizerEmail !== this.currentUserEmail) {
          this.$message.error('Hanya organizer yang dapat mengelola peserta event ini');
          return;
        }

        // Create updated attendees array (remove the participant) - backend expects array of email strings
        const currentAttendees = this.selectedEventForParticipants._rawEvent.attendees || [];
        const updatedAttendees = currentAttendees
          .filter(a => a.email !== row.email)
          .map(a => a.email);

        // Prepare update payload
        const updatePayload = {
          subject: this.selectedEventForParticipants.subject,
          body: this.selectedEventForParticipants._rawEvent.body?.content || this.selectedEventForParticipants._rawEvent.body || '',
          bodyType: 'HTML',
          startDateTime: this.selectedEventForParticipants.startDateTime,
          endDateTime: this.selectedEventForParticipants.endDateTime,
          timeZone: 'Asia/Jakarta',
          location: this.selectedEventForParticipants.location || '',
          attendees: updatedAttendees,
          isAllDay: this.selectedEventForParticipants._rawEvent.isAllDay || false,
          isOnlineMeeting: this.selectedEventForParticipants.isOnlineMeeting || false,
          onlineMeetingProvider: 'teamsForBusiness',
          reminderMinutesBeforeStart: this.selectedEventForParticipants._rawEvent.reminderMinutesBeforeStart || 15,
          showAs: this.selectedEventForParticipants._rawEvent.showAs || 'Busy'
        };

        console.log('Removing participant - Updating event:', {
          userIdOrUpn: this.currentUserEmail,
          eventId: eventId,
          removedEmail: row.email
        });

        // Call PATCH endpoint via Vuex action - use current user's email
        await this.updateEvent({
          userIdOrUpn: this.currentUserEmail,
          eventId: eventId,
          eventData: updatePayload
        });

        this.$message.success('Peserta berhasil dihapus!');
        
        // Reload participants and refresh events
        await this.fetchUserEvents(true); // silent refresh
        
        // Find updated event and reload participants
        const updatedEvent = this.events.find(e => e.id === eventId);
        if (updatedEvent) {
          this.selectedEventForParticipants = updatedEvent;
          this.loadParticipantsFromEvent();
        }
      } catch (error) {
        console.error('Error removing participant:', error);
        
        // Handle specific error cases
        if (error.response?.status === 403) {
          this.$message.error('Anda tidak memiliki izin untuk mengubah event ini. Hanya organizer yang dapat mengelola peserta.');
        } else if (error.response?.status === 404) {
          this.$message.error('Event tidak ditemukan');
        } else {
          this.$message.error('Gagal menghapus peserta: ' + (error.message || 'Unknown error'));
        }
      } finally {
        this.loadingParticipants = false;
      }
    },
    resetParticipantForm() {
      this.participantForm = {
        email: '',
        name: ''
      };
      if (this.$refs.participantForm) {
        this.$refs.participantForm.clearValidate();
      }
    },
    getResponseStatusText(status) {
      const statusMap = {
        'none': 'Belum Respon',
        'organizer': 'Penyelenggara',
        'tentativelyAccepted': 'Mungkin Hadir',
        'accepted': 'Akan Hadir',
        'declined': 'Tidak Hadir',
        'notResponded': 'Belum Respon'
      };
      return statusMap[status] || 'Tidak Diketahui';
    },
    getResponseTypeColor(status) {
      const colorMap = {
        'none': 'info',
        'organizer': 'primary',
        'tentativelyAccepted': 'warning',
        'accepted': 'success',
        'declined': 'danger',
        'notResponded': 'info'
      };
      return colorMap[status] || 'info';
    },
    openQRCodeDialog(row) {
      this.selectedEventForQR = { ...row };
      this.qrCodeData = null;
      this.qrCodeError = null;
      this.dialogQRCodeVisible = true;
    },
    async handleQRDialogOpened() {
      // Generate QR Code when dialog is opened
      await this.generateQRCodeFromAPI();
    },
    async generateQRCodeFromAPI() {
      if (!this.selectedEventForQR) return;

      this.loadingQRCode = true;
      this.qrCodeError = null;
      
      try {
        // Get organizer email and event ID
        const organizerEmail = this.selectedEventForQR.organizerEmail || this.currentUserEmail;
        const eventId = this.selectedEventForQR.id;
        
        if (!organizerEmail || !eventId) {
          throw new Error('Data event tidak lengkap');
        }

        // Build check-in form URL
        const baseUrl = window.location.origin; // e.g., http://localhost:8081
        const checkInUrl = `${baseUrl}/events/checkin?eventId=${eventId}&organizer=${organizerEmail}`;

        // Generate QR code as base64 data URL
        const qrCodeDataUrl = await QRCode.toDataURL(checkInUrl, {
          width: 300,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        });

        // Store QR code data
        this.qrCodeData = {
          eventId: eventId,
          eventSubject: this.selectedEventForQR.subject,
          checkInUrl: checkInUrl,
          qrCodeBase64: qrCodeDataUrl,
          message: 'QR Code berisi link ke form check-in. Peserta dapat scan QR code untuk mengisi form check-in.'
        };
        
        // Debug: Log response structure
        console.log('QR Code Generated:', this.qrCodeData);
        console.log('Check-In URL:', checkInUrl);
        
      } catch (error) {
        console.error('Error generating QR code:', error);
        this.qrCodeError = error.message || 'Gagal generate QR code';
        this.$message.error('Gagal generate QR code: ' + this.qrCodeError);
      } finally {
        this.loadingQRCode = false;
      }
    },
    async regenerateQRCode() {
      await this.generateQRCodeFromAPI();
    },
    formatQRExpiry(expiryDateTime) {
      if (!expiryDateTime) return '-';
      try {
        const date = new Date(expiryDateTime);
        return date.toLocaleString('id-ID', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        return '-';
      }
    },
    downloadQRCode() {
      const qrCodeSrc = this.qrCodeData?.qrCodeUrl || this.qrCodeData?.qrCodeBase64;
      
      if (qrCodeSrc) {
        // Create a temporary link to download the image
        const link = document.createElement('a');
        link.href = qrCodeSrc;
        link.download = `QR-Code-CheckIn-${this.selectedEventForQR.subject.replace(/[^a-z0-9]/gi, '_')}.png`;
        link.target = '_blank';
        
        // Trigger download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        this.$message.success('QR Code berhasil didownload!');
      } else {
        this.$message.warning('QR Code belum tersedia');
      }
    },
    copyAttendanceLink() {
      // Copy check-in URL from QR code data
      const url = this.qrCodeData?.checkInUrl;
      
      if (!url) {
        this.$message.warning('Link check-in belum tersedia');
        return;
      }
      
      // Copy to clipboard
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(() => {
          this.$message.success('Link check-in berhasil disalin ke clipboard!');
        }).catch(() => {
          this.fallbackCopyTextToClipboard(url);
        });
      } else {
        this.fallbackCopyTextToClipboard(url);
      }
    },
    fallbackCopyTextToClipboard(text) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        this.$message.success('Link berhasil disalin!');
      } catch (err) {
        this.$message.error('Gagal menyalin link');
      }
      document.body.removeChild(textArea);
    },
    resetForm() {
      this.form = {
        id: null,
        subject: '',
        body: '',
        bodyType: 'HTML',
        startDateTime: '',
        endDateTime: '',
        timeZone: 'Asia/Jakarta',
        location: '',
        attendees: [],
        isAllDay: false,
        isOnlineMeeting: false,
        onlineMeetingProvider: 'teamsForBusiness',
        reminderMinutesBeforeStart: 15,
        showAs: 'Busy'
      };
      if (this.$refs.eventForm) {
        this.$refs.eventForm.clearValidate();
      }
    }
  }
};
</script>

<style scoped>
/* Component-specific styles (shared styles moved to event-management.css) */
</style>
